import { createBrowserRouter, Outlet } from 'react-router';
import type { DataRouter, RouteObject } from 'react-router';
import { RootPage } from '../../standalone-app/pages/root/Root.ctrl';
import { GlobalErrorBoundary } from '@ingenico/react-mfe-shell-utils';
import { CheckUrlAddressPage, NotFoundPage } from '@ingenico/react-ui';
import { ALL_LNGS_NAMESPACES, DEFAULT_LNGS, SUPPORTED_LNGS } from '@ingenico/util-i18n-config';
import type { Observable } from 'rxjs';
import { BehaviorSubject, filter, firstValueFrom, lastValueFrom } from 'rxjs';
import { lazyContainerApiIt } from '../../standalone-app/hexa/hexa.util';
import type { ReactNode } from 'react';
import { useObservable } from '@ingenico/react-utils';
import type { CurrentUser } from '@ingenico/react-section-utils';
import { CurrentUserSubjectIt } from '@ingenico/util-mfe-shell';
import { LanguageSyncWithLocale } from '@ingenico/react-i18n';
import type { LazyContainerApi } from '@ingenico/util-mfe';
import type { MfeConfiguration } from '../../standalone-app/hexa/hexa.model';
import { appContainer } from '../../standalone-app/hexa/appContainer.const';
import { StandaloneLanguageService } from '../../standalone-app/services/standaloneLanguage.service';

async function lazyLoadMfeSectionRoutes(
  patch: (routeId: string | null, children: RouteObject[]) => void,
  lazyContainerApi: LazyContainerApi<MfeConfiguration>,
) {
  const routes = await lastValueFrom(lazyContainerApi.getDeviceManagementRoutes());
  patch('root', [
    {
      children: routes,
    },
  ]);
}

function AsyncOutlet({ isReady }: { readonly isReady: Observable<boolean> }): ReactNode {
  const { data } = useObservable(isReady);
  const user = appContainer.get<BehaviorSubject<CurrentUser | null>>(CurrentUserSubjectIt);
  const locale = StandaloneLanguageService.getSessionLanguage() ?? user.value?.locale ?? DEFAULT_LNGS;

  return (
    data === true && (
      <>
        <Outlet />
        <LanguageSyncWithLocale defaultLanguage={DEFAULT_LNGS} locale={locale} supportedLanguages={SUPPORTED_LNGS} />
      </>
    )
  );
}

export function deviceManagementRouter(): DataRouter {
  const hasLazyLoadedMfe$ = new BehaviorSubject<boolean>(false);
  const lazyContainerApi = appContainer.get<LazyContainerApi<MfeConfiguration>>(lazyContainerApiIt);

  return createBrowserRouter(
    [
      {
        path: '/:organizationName',
        element: <RootPage />,
        children: [
          {
            id: 'root',
            element: <AsyncOutlet isReady={hasLazyLoadedMfe$.asObservable()} />,
            errorElement: <GlobalErrorBoundary />,
            loader: async () => {
              await firstValueFrom(hasLazyLoadedMfe$.pipe(filter((loaded) => loaded)));
              return null;
            },
            children: [
              {
                path: '*',
                element: <NotFoundPage i18nNamespaces={[ALL_LNGS_NAMESPACES.ERROR]} label="404.description" />,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: <CheckUrlAddressPage i18nNamespaces={[ALL_LNGS_NAMESPACES.ERROR, ALL_LNGS_NAMESPACES.COMMON]} />,
      },
    ],
    {
      async patchRoutesOnNavigation({ patch }) {
        await lazyLoadMfeSectionRoutes(patch, lazyContainerApi);
        hasLazyLoadedMfe$.next(true);
      },
    },
  );
}
